'use strict';
import axios from 'axios';
import { isEmpty } from 'lodash';
class MapboxLogic {
    /**
     * Intent is to retrieve a post(zip) code from the consumed mapboxObject
     * @param {Object} mapboxObject - the mapboxObject from which to retrieve a postcode (if it exists)
     * @returns {String | null} representing the zip/postal code for the consumed mapboxObject (or null if no postcode exists on the consumed object)
     */
    getMapboxPostCode = (mapboxObject) => {
        if (mapboxObject.place_type[0] === 'postcode') {
            return mapboxObject.text;
        }

        const mapboxPostCodeIndex = this.getIndexByIdKey(mapboxObject, 'postcode');
        if (mapboxPostCodeIndex >= 0) {
            return this.retrieveMapboxContextValue(mapboxObject, mapboxPostCodeIndex);
        }

        return null; // we return null because there is no postcode on the input mapboxObject
    };

    /**
     * Constructs an array of promises to be resolved and then resolves the promises into an array of values
     * @param {Array} features - list of objects that represent mapbox 'features'
     * @returns {Array} - we return an array of feature objects which each have an available postcode
     */
    guaranteePostalCodes = async (features) => {
        const promises = this.getPromisesFromFeaturesArray(features);
        return this.getValuesFromPromisesArray(promises);
    };

    /**
     * Returns an array of objects, either feature objects which already have an available postcode
     * or promise objects which will call the reverse geocoding API when awaited
     * @param {Array} features - list of objects that represent mapbox 'features'
     * @returns {Array} - we return an array of promises
     */
    getPromisesFromFeaturesArray = (features) => {
        return features.map(async (feature) => {
            if (isEmpty(this.getMapboxPostCode(feature))) {
                return await this.getMapBoxFeatureFromCoordinates(feature);
            } else {
                return feature;
            }
        });
    };

    /**
     * Calls MapBox reverse geocoding API using coordinates from a feature object
     * @param {Object} feature - MapBox feature object presumed without postcode
     * @returns {Object} - we return a mapBox feature object expected to have a postcode
     */
    getMapBoxFeatureFromCoordinates = async (feature) => {
        const center = `${feature.center[0]},${feature.center[1]}`;
        const newResponse = await this.getMapboxAddressResponse(center);
        return newResponse.data.features[0];
    };

    /**
     * Resolves an array of promises and gets values from each promise
     * @param {Array} promises - array of promise objects
     * @returns {Array} - we return an array of resolved values for each promise
     */
    getValuesFromPromisesArray = async (promises) => {
        return Promise.allSettled(promises).then((promisedValues) => {
            return promisedValues.map((promisedValue) => {
                return promisedValue.value;
            });
        });
    };

    /**
     * Makes an HTTP GET request to mapbox and returns the result with all place types except country
     * @param {String} addressInput - an input string that is usually an address (but also excepts lng,lat coords as a string)
     * @returns {Promise<AxiosResponse>} - returns a promise of a response from the mapbox API endpoint
     */
    getMapboxAddressResponse = async (addressInput) => {
        const uriEncodedAddress = encodeURIComponent(addressInput);
        return await axios.get(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${uriEncodedAddress}.json?access_token=${process.env.MAPBOX_TOKEN}&country=us&fuzzyMatch=true&types=region,postcode,district,place,locality,neighborhood,address,poi`
        );
    };

    /**
     * Gets the integer index of an object in a mapboxObject for a given key
     * @param {Object} mapboxObject - the relevant mapboxObject that contains the context we will be searching on
     * @param {String} idKey - the key of context we wish to find, i.e. postcode
     * @returns {Integer} - index of the context that contains the idKey we're searching for, or -1 if it's not found
     */
    getIndexByIdKey = (mapboxObject, idKey) => {
        return mapboxObject.context.findIndex((context) => context.id.startsWith(idKey));
    };

    /**
     * Gets the value of the given index within the given mapbox object
     * @param {Object} mapboxObject - the mapbox object from which to grab a context value
     * @param {Integer} mapboxIndex - the index on the context from which to grab the value
     * @returns {any} the context value on mapboxObject at mapboxIndex
     */
    retrieveMapboxContextValue = (mapboxObject, mapboxIndex) => {
        return mapboxObject.context[mapboxIndex].text;
    };

    /**
     * Converts address Object to String
     * @param {Object} OBAPIAddress - member's address from OBAPI member profile response
     * @returns {String} - representing the obapi address
     */
    convertOBAPIAddress = (OBAPIAddress) => {
        if (
            OBAPIAddress.address1 &&
            OBAPIAddress.city &&
            OBAPIAddress.state &&
            OBAPIAddress.zipCode
        ) {
            return `${OBAPIAddress.address1}, ${OBAPIAddress.city}, ${OBAPIAddress.state} ${OBAPIAddress.zipCode}`;
        } else {
            return '';
        }
    };

    /**
     * Gets the place_name value of an autocomplete option for displaying the place name
     * @param {Object} option - from dropdown options
     * @returns {String} - the string label for an option
     */
    getLabelFromOption = (option) => {
        return `${option.place_name}`;
    };
}

export default new MapboxLogic();
